<template>
	<calculate-component :current="currentStep" :hasResult="hasResult">
		<!-- 侧边内容区域 -->
		<template #side>
			<a-form
				:model="formState"
				name="basic"
				style="width: 100%"
				layout="vertical"
				ref="formRef"
				autocomplete="off"
			>
				<div class="primary-title">
					基本信息
					<div class="primary-title-line"></div>
				</div>

				<a-row :gutter="12">
					<a-col :span="24">
						<a-form-item
							label="项目名称"
							name="name"
							:rules="[
								{
									required: true,
									message: '请输入项目名称'
								},
								{
									pattern: /^[\u4e00-\u9fa5a-zA-Z0-9-]+$/,
									whitespace: true,
									message: '项目名称格式错误'
								},
								{
									max: 40,
									message: '项目名称不得超过40字符'
								}
							]"
						>
							<a-textarea
								placeholder="请输入项目名称"
								v-model:value="formState.name"
								:disabled="inputDisabled"
								:autosize="{ minRows: 2, maxRows: 3 }"
							/>
						</a-form-item>
					</a-col>
					<a-col :span="24">
						<a-form-item
							label="项目所属电价地区"
							:label-col="{
								style: {
									width: '150px'
								}
							}"
							name="areaId"
							:rules="[
								{
									required: true,
									message: '请选择地区'
								}
							]"
						>
							<a-cascader
								v-model:value="formState.areaId"
								:options="areaList"
								placeholder="请选择地区"
								size="large"
								:field-names="{
									label: 'name',
									value: 'id',
									children: 'sub'
								}"
								:disabled="inputDisabled"
							/>
						</a-form-item>
					</a-col>
				</a-row>
				<div class="primary-title">
					<div class="font">
						用电数据
						<a-tooltip
							placement="right"
							:overlayStyle="{
								width: '218px'
							}"
						>
							<template #title
								>上传需要配储的负载数据进行分析，建议取最近一年数据</template
							>
							<span class="tooltip-cion"><InfoOutlined /></span>
						</a-tooltip>
					</div>
					<div class="primary-title-line"></div>
				</div>
				<a-row style="margin-top: 16px" :gutter="12">
					<a-col :span="24">
						<a-form-item
							label="数据模板"
							name="template"
							:rules="[
								{
									required: true,
									message: '请选择数据模板'
								}
							]"
						>
							<a-select
								v-model:value="formState.template"
								:disabled="inputDisabled"
								size="large"
								:getPopupContainer="
									triggerNode => triggerNode.parentNode
								"
							>
								<a-select-option value="default"
									>轻舟模板</a-select-option
								>
							</a-select>
						</a-form-item>
					</a-col>
					<a-col :span="24">
						<a-form-item
							label="用电数据上传"
							name="fileList"
							:rules="[
								{
									required: true,
									message: '请上传用电数据！'
								}
							]"
						>
							<a-upload
								:file-list="fileList"
								:before-upload="beforeUpload"
								:custom-request="handleCustomRequest"
								@remove="handleRemove"
								:multiple="true"
								:disabled="inputDisabled"
								:max-count="12"
								accept=".csv,.xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
							>
								<a-button
									class="btn-default upload-btn"
									:disabled="inputDisabled"
								>
									<upload-outlined></upload-outlined>
									上传文件
								</a-button>
								<a-button
									style="margin-left: 8px"
									:disabled="inputDisabled"
									@click="downloadFile"
									class="btn-default download-btn"
								>
									<DownloadOutlined />
									下载数据模板
								</a-button>
								<div class="upload-font">
									支持多个文件上传，格式为csv、xlsx、xls
								</div>
							</a-upload>
						</a-form-item>
					</a-col>
					<a-col :span="24">
						<a-form-item
							label="变压器容量"
							name="tfmCapacity"
							:rules="[
								{
									required: true,
									message: '请输入变压器容量'
								}
							]"
							extra="上传的负载数据对应的变压器容量"
						>
							<a-input
								placeholder="请输入变压器容量"
								v-model:value="formState.tfmCapacity"
								@input="inputChange('tfmCapacity', 2)"
								@blur="blur('tfmCapacity')"
								:disabled="inputDisabled"
								size="large"
							>
								<template #suffix> kVA </template>
							</a-input>
						</a-form-item>
					</a-col>
				</a-row>
			</a-form>
			<!-- 开始 && 重新分析 -->
			<a-button
				style="width: 100%"
				class="bottom-btn"
				type="primary"
				:loading="loading == 1"
				@click="startAnalysis()"
				:disabled="inputDisabled"
				v-if="[0, 1].includes(loading)"
			>
				<span v-if="loading === 0">开始负载分析</span>
				<span v-if="loading === 1">正在分析…</span>
			</a-button>
			<a-button
				style="width: 100%"
				class="bottom-btn"
				type="primary"
				:loading="loading == 1"
				@click="startAnalysis(loadId)"
				:disabled="inputDisabled"
				v-if="[2, 3].includes(loading)"
			>
				重新开始分析
			</a-button>
		</template>

		<!-- 右边内容区域 -->
		<template #content>
			<div class="primary-title">
				<div class="title-left">
					<div style="width: 500px;" class="font">
						负载分析结果
						<div v-if="loading == 2" class="title-tips">
							数据范围：{{ analysis.startDate }} 至
							{{ analysis.endDate }} 共{{ analysis.total }}天
						</div>
					</div>
					<div class="primary-title-line"></div>
				</div>

				<div class="title-right">
					<!-- 分析成功 -->
					<div class="succcess-tips" v-if="loading == 2 && showTips">
						<span class="left-icon">
							<img
								src="@/assets/images/success-icon.png"
								alt=""
							/>
						</span>
						负载分析已完成，点击“下一步”进行配储评估
						<span
							class="right-icon"
							@click="() => (showTips = !showTips)"
						>
							<CloseOutlined />
						</span>
					</div>

					<!-- 分析失败 -->
					<div class="error-tips" v-if="loading == 3">
						<span class="left-icon">
							<img src="@/assets/images/fail-icon.png" alt="" />
						</span>
						数据出错，负载分析失败
					</div>
				</div>
			</div>
			<!-- 分析结果 -->
			<div class="result">
				<!-- 分析未开始 -->
				<div v-if="loading == 0" class="status-img">
					<img src="@/assets/images/empty_2.png" alt="" />
					<div class="info">
						<div class="info-title">暂无结果</div>
						<div class="second-title">
							请先完成左侧内容填写，并进行负载分析
						</div>
					</div>
				</div>
				<!-- 分析进行中 -->
				<div v-if="loading == 1" class="status-img">
					<!-- 动画区域 -->
					<div id="svgaA" class="svga"></div>
					<div class="info">
						<div class="load-title">负载分析中</div>
					</div>
				</div>
				<!-- 分析完成 -->
				<div v-if="loading > 1" class="finish beautify_scroll">
					<div v-if="currentStep != 0.3" class="inner-box">
						<!-- 电量数据概览 -->
						<div class="echarts-box">
							<div class="title">
								电量数据概览
								<a-tooltip placement="right">
									<template #title
										>概览每天用电量变化情况</template
									>
									<span class="tooltip-cion"
										><InfoOutlined
									/></span>
								</a-tooltip>
							</div>
							<div class="echart">
								<echarts-component
									:options="electricOption"
								></echarts-component>
							</div>
							<div class="presentation-box">
								<div class="header">
									<div class="box">
										<div class="icon">
											<img
												src="@/assets/images/result_icon1.png"
												alt=""
											/>
										</div>
										<div class="label">有效数据：</div>
										<div class="value">
											{{ presentation.loadValidDays }}天
										</div>
									</div>
									<div class="box">
										<div class="icon">
											<img
												src="@/assets/images/result_icon2.png"
												alt=""
											/>
										</div>
										<div class="label">总用电量：</div>
										<div class="value">
											{{
												toFixed(
													presentation.sumElec / 1000,
													2
												)
											}}MWh
										</div>
									</div>
									<div class="box">
										<div class="icon">
											<img
												src="@/assets/images/result_icon3.png"
												alt=""
											/>
										</div>
										<div class="label">日平均电量：</div>
										<div class="value">
											{{ presentation.meanElec }}kWh
										</div>
									</div>
								</div>

								<div class="bottom">
									<div class="bottom-left">
										<div class="inner-title">
											年尖峰平谷电量对比
										</div>
										<echarts-component
											:options="pieOptions"
										></echarts-component>
									</div>
									<div class="bottom-right">
										<div
											class="info-box"
											v-if="presentData.length > 0"
										>
											<div
												class="line"
												v-for="(
													item, index
												) in presentData"
												:key="index"
											>
												<div class="label">
													{{ item.title }}
												</div>
												<div class="value">
													{{ item.value }}
												</div>
											</div>
										</div>
										<div v-else class="empty">暂无数据</div>
									</div>
								</div>
							</div>
						</div>
						<!-- 典型日负荷 -->
						<div class="echarts-box">
							<div class="title">
								典型日负荷

								<!-- <a-tooltip>
									<template #title
										>典型日负荷是算法根据负载数据聚合出的特征曲线，代表了负荷在一天内波动的情况。</template
									>
									<span class="tooltip-cion"
										><InfoOutlined
									/></span>
								</a-tooltip> -->
							</div>
							<div class="tips">
								tips：典型日负荷是系统根据负载数据聚合出的特征曲线，代表了负荷在一天内波动的情况。一个典型日可代表多天的数据特征。
							</div>
							<div class="echart">
								<echarts-component
									:options="dayOption"
								></echarts-component>
							</div>
						</div>
					</div>
					<div v-else class="status-img">
						<img src="@/assets/images/empty_2.png" alt="" />
						<div class="info">
							<div class="info-title">暂无数据</div>
							<div class="second-title">
								数据出错，负载分析失败
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<!-- 功能按钮 -->
		<template #action-btn>
			<a-button
				:disabled="!(loading === 2)"
				type="primary"
				@click="nextClick"
				>下一步</a-button
			>
		</template>
	</calculate-component>
</template>

<script setup>
	import { onMounted, onBeforeUnmount, reactive, ref } from "vue";
	import {
		DownloadOutlined,
		UploadOutlined,
		InfoOutlined,
		CloseOutlined
	} from "@ant-design/icons-vue";
	// 引入组件
	import CalculateComponent from "../../components/CalculateBox.vue";
	import EchartsComponent from "@/components/EchartsComponent.vue";
	// 引入公共方法
	import { toFixed, defaultOptions, setNotOpt } from "@/plugins/index.js";
	// 引入router
	import { useRouter, useRoute } from "vue-router";
	// 引入vuex
	import { useStore } from "vuex";
	const store = useStore();
	// 判断url中是否有id，有则是继续
	const route = useRoute();
	let router = useRouter();
	// 引入动画库
	import { Player as SVGAPlayer, Parser as SVGAParser } from "svgaplayerweb";
	// 引入接口
	import {
		getArea,
		uploadFile,
		startLoadAnalysis,
		getElectricData,
		getTypicalData,
		saveNextStep
	} from "@/api/calculate.js";
	import { getProjectDetail } from "@/api/projects";
	// 引入message
	import { message } from "ant-design-vue";
	// 引入解密
	import { decryptData, encryptData } from "@/plugins/index.js";
	import { electricOverview, typicalDayOption } from "../../plugin";
	// 地区数据
	let areaList = ref([]);

	// 分析按钮状态  0未开始 1分析中 2分析结束 3分析失败
	let loading = ref(0);
	let inputDisabled = ref(false);

	// 记录step的状态
	let currentStep = ref(0);

	// 记录是否结果
	let hasResult = ref(false);

	let showTips = ref(true);

	// 动画
	let parser = null;

	// 表单数据
	let formState = reactive({
		name: "",
		areaId: [],
		template: "default",
		tfmCapacity: "",
		fileList: []
	});
	let formRef = ref();

	// 记录当前负载分析返回的id
	let loadId = ref(null);

	// 记录轮询的标识
	let travelId = ref(null);

	// 记录天数
	let analysis = reactive({
		startDate: "-",
		endDate: "-",
		total: "-"
	});

	// 有效数据
	let presentation = reactive({
		loadValidDays: "-",
		sumElec: "-",
		meanElec: "-",
		sharp: "-",
		peak: "-",
		flat: "-",
		valley: "-"
	});

	// 文件列表
	let fileList = ref([]);

	// 电量数据
	let electricOption = ref({});

	// 典型日数据
	let dayOption = ref({});

	// 电量对比
	let pieOptions = ref({});

	// 统计数据
	let presentData = ref([]);

	// 分析失败信息
	const errorMsg = ref("分析失败！");

	// 记录项目步骤
	const projectStep = ref(null);

	onBeforeUnmount(() => {
		clearInterval(travelId.value);
		travelId.value = undefined;
		// 更新store中的breadcrumb信息
		store.commit("updateBreadCrumbInfo", null);
	});

	onMounted(() => {
		// 获取地区
		getAreaList();

		const { id, step } = route.query;
		// 更新store中的breadcrumb信息
		store.commit("updateBreadCrumbInfo", {});

		// 存储当前项目的step
		if (step) {
			projectStep.value = decryptData(step);
		}
		if (id) {
			loadId.value = decryptData(id);
			// 查询详情
			getProjectDetailData();
		}
	});

	// 下一步
	let nextClick = () => {
		// 获取当前的进度
		const { projectInfo } = store.state;
		console.log(projectInfo);
		const { step } = projectInfo;
		if (step > 1) {
			router.replace({
				path: "/scheme",
				query: {
					id: encryptData(loadId.value),
					tfmCapacity: encryptData(formState.tfmCapacity),
                    step: encryptData(2)
				}
			});
		} else {
			saveNextStep(loadId.value).then(res => {
				message.success("负载分析保存成功！");
				// 更新vuex中项目信息
				store.commit("updateProjectInfo", {
					id: loadId.value,
					step: 2,
					tfmCapacity: formState.tfmCapacity
				});
				// 更新store中的项目步骤
				currentStep.value = 1;
				store.commit("updateProjectStep", 1);
				setTimeout(() => {
					router.replace({
						path: "/scheme",
						query: {
							id: encryptData(loadId.value),
							tfmCapacity: encryptData(formState.tfmCapacity)
						}
					});
				}, 100);
			});
		}
	};

	// 更新inputDisabled状态
	let updateInputDisabledStatus = (val = false) => {
		// 获取step
		const { step } = store.state.projectInfo;
		let res = false;
		// 大于该步骤时，不允许修改任何参数
		if (step && step > 1) {
			res = true;
		} else {
			res = val;
		}
		inputDisabled.value = res;
	};

	// 开始负载分析
	let startAnalysis = (id = null) => {
		// 校验数据
		formRef.value.validate().then(datas => {
			// 状态改为分析中
			loading.value = 1;
            // 清空数据
            clearPresentation();
            pieOptions.value = {}
            presentData.value = [];
            electricOption.value = {}
            dayOption.value = {}
			// 更新store中的项目步骤
			currentStep.value = 0;
			store.commit("updateProjectStep", 0);
			// 开始动画
			setTimeout(() => {
				startAnimation();
			}, 100);
			let { name, areaId, fileList, tfmCapacity } = datas;
			let list = [];
			fileList.map(el => {
				list.push({
					filename: el.name,
					fileDownloadUrl: el.url
				});
			});
			// 不论是重新开始还是开始都将输入框跟按钮禁用
			// inputDisabled.value = true;
			updateInputDisabledStatus(true);
			startLoadAnalysis({
				id,
				name,
				areaId: areaId[areaId.length - 1],
				files: list,
				tfmCapacity
			})
				.then(res => {
					// 记录此时的负载id
					loadId.value = res;
					// 更新vuex中项目信息
					store.commit("updateProjectInfo", {
						id: loadId.value,
						step: 1,
						tfmCapacity
					});
					// 更新store中的breadcrumb信息
					store.commit("updateBreadCrumbInfo", {
						name
					});
					startTravelSearch();
					router.replace({
						path: route.path,
						query: {
							id: encryptData(loadId.value)
						}
					});
				})
				.catch(err => {
					// 状态改为分析中
					loading.value = 3;
					// inputDisabled.value = false;
					updateInputDisabledStatus(false);
					currentStep.value = 0.3;
					// 更新store中的项目步骤
					store.commit("updateProjectStep", 0.3);

					hasResult.value = true;
					// 图表设置为空数据
					setTimeout(() => {
						let emptyOption = setNotOpt();
						electricOption.value = emptyOption;
						dayOption.value = emptyOption;
						pieOptions.value = emptyOption;
						clearPresentation();

						presentData.value = [];
					}, 100);
				});
		});
	};

	// 清空
	const clearPresentation = () => {
		presentation.loadValidDays = "-";
		presentation.sumElec = "-";
		presentation.meanElec = "-";
		presentation.sharp = "-";
		presentation.peak = "-";
		presentation.flat = "-";
		presentation.valley = "-";
	};

	// 开启轮询
	const startTravelSearch = () => {
		// 查询电量数据
		getElectricDataList();
		travelId.value = setInterval(() => {
			getElectricDataList();
		}, 3000);
	};

	// 查询电量概览
	let getElectricDataList = () => {
		getElectricData(loadId.value)
			.then(res => {
				if (res) {
					// 判断算法是否报错
					if (res.errorFlag) {
						hasResult.value = true;
						clearInterval(travelId.value);
						travelId.value = undefined;
						loading.value = 3;
						// inputDisabled.value = false;
						updateInputDisabledStatus(false);
						currentStep.value = 0.3;
						// 更新store中的项目步骤
						store.commit("updateProjectStep", 0.3);
						errorMsg.value = res.analysisMsg;
						message.error(res.analysisMsg);
						// 图表设置为空数据
						setTimeout(() => {
							let emptyOption = setNotOpt();
							electricOption.value = emptyOption;
							dayOption.value = emptyOption;
							pieOptions.value = emptyOption;
							clearPresentation();
							presentData.value = [];
						}, 100);
						return;
					} else {
						// 不论是重新开始还是开始都将输入框跟按钮禁用
						// inputDisabled.value = true;
						updateInputDisabledStatus(true);

						const {} = res;
					}

					if (res.dayElec) {
						clearInterval(travelId.value);
						travelId.value = undefined;
						analysis.startDate = res.startDate;
						analysis.endDate = res.endDate;
						analysis.total = res.loadDays;

						const {
							loadValidDays,
							sumElec,
							meanElec,
							sharp,
							peak,
							flat,
							valley,
							ravine
						} = res;
						// 记录统计数据
						presentation.loadValidDays = loadValidDays;
						presentation.sumElec = sumElec;
						presentation.meanElec = meanElec;
						presentation.sharp = sharp;
						presentation.peak = peak;
						presentation.flat = flat;
						presentation.valley = valley;
						presentation.ravine = ravine;

						// 绘制饼图
						drawPresentationPie();

						// 绘制侧边表格
						makePresentationTable();

						// 开始轮询查找典型日负荷
						getTypicalDataList();
						travelId.value = setInterval(() => {
							getTypicalDataList();
						}, 3000);

						// 成功,渲染echarts
						electricOption.value = electricOverview(
							res.dayElec,
							meanElec
						);
					}
				}
			})
			.catch(err => {
				console.log(err);
			});
	};

	// 查找典型日负荷
	let getTypicalDataList = () => {
		getTypicalData(loadId.value)
			.then(res => {
				if (res) {
					// 判断算法是否报错
					if (res.errorFlag) {
						hasResult.value = true;
						clearInterval(travelId.value);
						travelId.value = undefined;
						loading.value = 3;
						updateInputDisabledStatus(false);
						// inputDisabled.value = false;
						currentStep.value = 0.3;
						// 更新store中的项目步骤
						store.commit("updateProjectStep", 0.3);

						errorMsg.value = res.analysisMsg;
						message.error(res.analysisMsg);
						// 图表设置为空数据
						setTimeout(() => {
							let emptyOption = setNotOpt();
							// electricOption.value = emptyOption;
							dayOption.value = emptyOption;
						}, 100);
						return;
					}

					if (res.typicalDayCurves) {
						// 成功
						// 停止轮询
						clearInterval(travelId.value);
						travelId.value = undefined;
						loading.value = 2;
						currentStep.value = 0.5;
						// 更新store中的项目步骤
						store.commit("updateProjectStep", 0.5);
						hasResult.value = true;

						// 渲染图表
						dayOption.value = typicalDayOption(
							res.typicalDayCurves,
							res.tfmCapacity
						);
					}
					updateInputDisabledStatus(false);
					// inputDisabled.value = false;
				}
			})
			.catch(err => {
				console.log(err);
			});
	};

	// 文件上传 && 删除
	let handleRemove = file => {
		let index = fileList.value.indexOf(file);
		let newFileList = fileList.value.slice();
		newFileList.splice(index, 1);
		fileList.value = newFileList;
		// 给表单赋值，防止报错
		formState.fileList = fileList.value;
		formRef.value.validateFields(["fileList"]);
	};

	// 拦截自定义上传
	let beforeUpload = async file => {
		// 校验文件类型
		let arr = file.name.split(".");
		const filleType = arr[arr.length - 1];
		const acceptType = ["xlsx", "xls", "csv"];
		if (acceptType.includes(filleType)) {
			// 校验是否重名
			let allNames = [];
			fileList.value.map(el => {
				allNames.push(el.name);
			});
			if (allNames.includes(file.name)) {
				message.warning("文件存在同名，请检查！");
				return false;
			} else {
				let obj = {
					name: file.name,
					status: "uploading",
					url: undefined
				};
				fileList.value = [...(fileList.value || []), obj];
				// 给表单赋值，防止报错
				formState.fileList = fileList.value;
				return true;
			}
		} else {
			message.warning("文件格式不对，请上传xlsx,xls或csv文件！");
			return false;
		}
	};

	// 自定义上传事件
	const handleCustomRequest = async ({ file, onSuccess, onError }) => {
		// 先push进文件列表
		await uploadFile({
			file
		})
			.then(res => {
				let { filename, fileDownloadUrl } = res;
				fileList.value.map(file => {
					if (file.name == filename) {
						file.status = "done";
						file.url = fileDownloadUrl;
					}
				});
				formState.fileList = fileList.value;
			})
			.catch(err => {
				file.status = "error";
				const { name } = file;
				fileList.value = fileList.value.filter(
					file => file.name !== name
				);

				// 给表单赋值，防止报错
				formState.fileList = fileList.value;
				formRef.value.validateFields(["fileList"]);

				onError();
			});
	};

	// 下载文件
	let downloadFile = () => {
		let fileUrl =
			"https://skiffenergy-estimation.oss-cn-zhangjiakou.aliyuncs.com/测算数据用电功率模板.xlsx";
		// 创建一个 <a> 元素
		let link = document.createElement("a");
		link.href = fileUrl;

		// 添加到 DOM 中并模拟点击下载
		document.body.appendChild(link);
		link.click();

		// 清理资源
		URL.revokeObjectURL(link.href);
		document.body.removeChild(link);
	};

	// 获取地区数据
	let getAreaList = () => {
		getArea().then(res => {
			areaList.value = res || [];
		});
	};

	// 查询详情
	const getProjectDetailData = () => {
		hasResult.value = false;
		getProjectDetail({
			id: loadId.value,
			toStep: projectStep.value
		}).then(res => {
			const { loadDetailVO, name } = res;
			// 更新store中的breadcrumb信息
			store.commit("updateBreadCrumbInfo", {
				name
			});
			if (loadDetailVO) {
				const {
					provinceCode,
					areaId,
					files,
					tfmCapacity,
					step,
					analysisMsg
				} = loadDetailVO;
				let fileListData = [];
				files.map(file => {
					fileListData.push({
						name: file.filename,
						url: file.fileDownloadUrl,
						status: "done"
					});
				});
				formState.name = name;
				formState.areaId = [provinceCode, areaId];
				formState.tfmCapacity = tfmCapacity;
				fileList.value = fileListData;
				formState.fileList = fileListData;

				// 算法抛错
				if (step == 2) {
					// message.warning("算法分析失败");
					message.error(analysisMsg);
					// inputDisabled.value = false;
					updateInputDisabledStatus(false);
					loading.value = 3; // 失败
					currentStep.value = 0.3;
					// 更新store中的项目步骤
					store.commit("updateProjectStep", 0.3);
					errorMsg.value = analysisMsg;
					// 图表设置为空数据
					setTimeout(() => {
						let emptyOption = setNotOpt();
						electricOption.value = emptyOption;
						pieOptions.value = emptyOption;
						clearPresentation();
						presentData.value = [];
						dayOption.value = emptyOption;
					}, 100);
					hasResult.value = true;
				} else {
					// 状态改为分析中
					loading.value = 1;
					setTimeout(() => {
						startAnimation();
					}, 100);

					// 开始轮询
					startTravelSearch();
				}
			}
		});
	};

	/* 数据处理 */
	// 最多两位小数
	let inputChange = props => {
		var isValid = /^\d+(\.\d{0,2})?$/.test(formState[props]);

		if (!isValid) {
			// 如果输入不符合要求，只保留有效的数字部分
			let validValue = formState[props].match(/^\d+(\.\d{0,2})?/);
			formState[props] = validValue ? validValue[0] : "";
		}
	};

	// 离开时校验最后是否为小数点
	let blur = props => {
		if (formState[props].endsWith(".")) {
			formState[props] = formState[props].slice(0, -1);
		}
	};

	// 绘制饼图
	let drawPresentationPie = () => {
		// 成功
		let option = {
			...JSON.parse(JSON.stringify(defaultOptions))
		};
		option.series = [
			{
				name: "电量",
				data: [
					{ value: presentation.sharp, name: "尖" },
					{ value: presentation.peak, name: "峰" },
					{ value: presentation.flat, name: "平" },
					{ value: presentation.valley, name: "谷" },
					{ value: presentation.ravine, name: "深谷" }
				],
				type: "pie",
				label: {
					formatter: "{b} ({d}%)"
				}
			}
		];
		option.legend.show = false;
		option.tooltip = {
			trigger: "item",
			formatter: params => {
				const { name, value } = params.data;
				let precent = toFixed(
					Number(value / presentation.sumElec) * 100,
					2
				);
				let str = `<span style='font-weight:600'>用电量</span>  <br> ${params.marker}${name}：${precent}%   <span style='font-weight:600;margin-left:16px;'>${value}</span>`;
				return str;
			}
		};
		option.color = ["#EF6D6E", "#F8C858", "#1376D2", "#7C92BA","#add5e2"];

		// 渲染图表
		pieOptions.value = option;
	};

	// 绘制表格
	let makePresentationTable = () => {
		console.log(presentation.ravine);
		presentData.value = [
			{
				title: "总用电量",
				value: `${toFixed(presentation.sumElec / 1000, 2)}MWh`
			},
			{
				title: "日平均电量",
				value: `${toFixed(presentation.meanElec, 2)}kWh`
			},
			{
				title: "尖电量",
				value: `${toFixed(presentation.sharp, 2)}kWh`
			},
			{
				title: "峰电量",
				value: `${toFixed(presentation.peak, 2)}kWh`
			},
			{
				title: "平电量",
				value: `${toFixed(presentation.flat, 2)}kWh`
			},
			{
				title: "谷电量",
				value: `${toFixed(presentation.valley, 2)}kWh`
			},
			{
				title: "深谷电量",
				value: `${toFixed(presentation.ravine, 2)}kWh`
			}
		];
	};

	/* 动画 */
	// 加载动画
	let startAnimation = () => {
		parser = new SVGAParser();
		const container = document.getElementById("svgaA");
		parser.load("/svga/loading.svga", videoItem => {
			const player = new SVGAPlayer(container);
			player.setVideoItem(videoItem);
			player.startAnimation();
		});
	};
</script>

<style lang="less" scoped>
	@import "./style/index.less";
</style>
